"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { IconBan, IconChevronRight, IconDotsVertical, IconKey, IconPencil, IconTrash, IconUserKey } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/ui/dropdown-menu";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { PersonalAccessTokenActionDialogs, type PersonalAccessTokenOwnerAction } from "@features/personal-access-tokens/ui/personal-access-token-action-dialogs";
import { classifyToken } from "@features/personal-access-tokens/personal-access-token-view-model";
import type { PersonalAccessToken, PersonalAccessTokenScope, PersonalAccessTokenStatus } from "@features/personal-access-tokens/types";
type TokenFilter = "all" | "working" | "not-working";
const statusTranslationKeys = {
  active: "status.active",
  suspended: "status.suspended",
  revoked: "status.revoked"
} satisfies Record<PersonalAccessTokenStatus, `status.${PersonalAccessTokenStatus}`>;
const scopeTranslationKeys = {
  api_read: "scopes.api_read",
  api_write: "scopes.api_write",
  api_destroy: "scopes.api_destroy",
  mcp_read: "scopes.mcp_read",
  mcp_write: "scopes.mcp_write"
} satisfies Record<PersonalAccessTokenScope, `scopes.${PersonalAccessTokenScope}`>;
interface PersonalAccessTokenListProps {
  readonly tokens: readonly PersonalAccessToken[];
  readonly isPending: boolean;
  readonly onRename: (id: number, name: string) => Promise<void>;
  readonly onRevoke: (id: number, reason?: string) => Promise<"already-revoked" | void>;
  readonly onDelete: (id: number) => Promise<void>;
  readonly onIssue?: () => void;
}
const formatDate = (value: string, locale: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: "UTC"
  }).format(date);
};
export const PersonalAccessTokenList = t0 => {
  const $ = _c(90);
  const {
    tokens,
    isPending,
    onRename,
    onRevoke,
    onDelete,
    onIssue
  } = t0;
  const t = useTranslations("features.personal-access-tokens.ui.ownerTokensPage.list");
  const pageT = useTranslations("features.personal-access-tokens.ui.ownerTokensPage");
  const locale = useLocale();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  let t1;
  if ($[0] !== filter || $[1] !== locale || $[2] !== search || $[3] !== tokens) {
    const normalizedSearch = search.trim().toLocaleLowerCase(locale);
    let t2;
    if ($[5] !== filter) {
      t2 = token_1 => {
        const isWorking = classifyToken(token_1) === "working";
        return filter === "all" || (filter === "working" ? isWorking : !isWorking);
      };
      $[5] = filter;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = tokens.filter(_temp).filter(token_0 => normalizedSearch ? token_0.name.toLocaleLowerCase(locale).includes(normalizedSearch) : true).filter(t2);
    $[0] = filter;
    $[1] = locale;
    $[2] = search;
    $[3] = tokens;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const visibleTokens = t1;
  let t2;
  if ($[7] !== statusMessage) {
    t2 = statusMessage ? <p role="status" className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">{statusMessage}</p> : null;
    $[7] = statusMessage;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const statusAnnouncement = t2;
  if (tokens.length === 0 || tokens.every(_temp2)) {
    let t3;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <span className="mb-4 flex size-11 items-center justify-center rounded-lg bg-muted"><IconKey aria-hidden="true" className="size-5 text-muted-foreground" /></span>;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== t) {
      t4 = t("empty.title");
      $[10] = t;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== t4) {
      t5 = <h2 className="font-semibold">{t4}</h2>;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== t) {
      t6 = t("empty.description");
      $[14] = t;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== t6) {
      t7 = <p className="mt-2 max-w-md text-sm text-muted-foreground">{t6}</p>;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== onIssue || $[19] !== pageT) {
      t8 = onIssue && <Button className="mt-5" onClick={onIssue}>{pageT("issue.openButton")}</Button>;
      $[18] = onIssue;
      $[19] = pageT;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== t5 || $[22] !== t7 || $[23] !== t8) {
      t9 = <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border bg-card p-8 text-center">{t3}{t5}{t7}{t8}</div>;
      $[21] = t5;
      $[22] = t7;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== statusAnnouncement || $[26] !== t9) {
      t10 = <div className="space-y-4">{statusAnnouncement}{t9}</div>;
      $[25] = statusAnnouncement;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  let t3;
  if ($[28] !== t) {
    t3 = t("search.label");
    $[28] = t;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== t3) {
    t4 = <Label htmlFor="personal-access-token-search">{t3}</Label>;
    $[30] = t3;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  let t5;
  if ($[32] !== t) {
    t5 = t("search.placeholder");
    $[32] = t;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  let t6;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => setSearch(event.target.value);
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== search || $[36] !== t5) {
    t7 = <Input id="personal-access-token-search" type="search" value={search} placeholder={t5} onChange={t6} />;
    $[35] = search;
    $[36] = t5;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== t4 || $[39] !== t7) {
    t8 = <div className="grid gap-2">{t4}{t7}</div>;
    $[38] = t4;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t) {
    t9 = t("filter.label");
    $[41] = t;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== t9) {
    t10 = <Label htmlFor="personal-access-token-filter">{t9}</Label>;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_0 => setFilter(event_0.target.value as TokenFilter);
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t) {
    t12 = t("filter.all");
    $[46] = t;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t12) {
    t13 = <option value="all">{t12}</option>;
    $[48] = t12;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t) {
    t14 = t("filter.working");
    $[50] = t;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t14) {
    t15 = <option value="working">{t14}</option>;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== t) {
    t16 = t("filter.notWorking");
    $[54] = t;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== t16) {
    t17 = <option value="not-working">{t16}</option>;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== filter || $[59] !== t13 || $[60] !== t15 || $[61] !== t17) {
    t18 = <select id="personal-access-token-filter" value={filter} onChange={t11} className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">{t13}{t15}{t17}</select>;
    $[58] = filter;
    $[59] = t13;
    $[60] = t15;
    $[61] = t17;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== t10 || $[64] !== t18) {
    t19 = <div className="grid gap-2">{t10}{t18}</div>;
    $[63] = t10;
    $[64] = t18;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== t19 || $[67] !== t8) {
    t20 = <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem]">{t8}{t19}</div>;
    $[66] = t19;
    $[67] = t8;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== expandedId || $[70] !== locale || $[71] !== t || $[72] !== visibleTokens) {
    t21 = visibleTokens.length === 0 ? <p role="status" className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{t("noResults")}</p> : <div className="overflow-hidden rounded-lg border bg-card"><div className="hidden grid-cols-[minmax(11rem,2fr)_minmax(6rem,1fr)_minmax(7rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_2rem] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground uppercase xl:grid"><span>{t("details.nameLabel")}</span><span>{t("details.scopeLabel")}</span><span>{t("details.workspacesLabel")}</span><span>{t("details.statusLabel")}</span><span>{t("details.expiresAtLabel")}</span><span>{t("details.lastUsedAtLabel")}</span><span className="sr-only">{t("actions.open")}</span></div>{visibleTokens.map(token_3 => {
        const isExpanded = expandedId === token_3.id;
        const statusKey = statusTranslationKeys[token_3.status];
        return <article key={token_3.id} className="border-b last:border-b-0"><div className="grid gap-3 p-4 xl:grid-cols-[minmax(11rem,2fr)_minmax(6rem,1fr)_minmax(7rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_minmax(6rem,1fr)_2rem] xl:items-center"><div className="min-w-0"><span className="flex flex-wrap items-center gap-2"><span className="font-medium">{token_3.name}</span>{token_3.issuedByAdministrator && <Badge variant="secondary" className="gap-1"><IconUserKey aria-hidden="true" />{t("details.issuedByAdministrator")}</Badge>}</span><code className="mt-1 block font-mono text-xs break-all text-muted-foreground">{token_3.maskedSecret}</code></div><span className="text-sm xl:block"><span className="mr-2 text-xs text-muted-foreground xl:hidden">{t("details.scopeLabel")}:</span>{token_3.scopes.map(scope => t(scopeTranslationKeys[scope])).join(", ")}</span><span className="text-sm"><span className="mr-2 text-xs text-muted-foreground xl:hidden">{t("details.workspacesLabel")}:</span>{token_3.workspaceIds.length === 0 ? t("allWorkspaces") : token_3.workspaceIds.join(", ")}</span><span role="group" aria-label={[t(statusKey), token_3.expired ? t("status.expired") : undefined, token_3.signedWithPreviousKey ? t("status.previousKey") : undefined].filter(Boolean).join(", ")} className="flex flex-wrap gap-1"><Badge variant={classifyToken(token_3) === "working" ? "default" : "secondary"}>{t(statusKey)}</Badge>{token_3.expired && <Badge variant="outline">{t("status.expired")}</Badge>}{token_3.signedWithPreviousKey && <Badge variant="outline">{t("status.previousKey")}</Badge>}</span><span className="text-sm"><span className="mr-2 text-xs text-muted-foreground xl:hidden">{t("details.expiresAtLabel")}:</span>{formatDate(token_3.expiresAt, locale)}</span><span className="text-sm"><span className="mr-2 text-xs text-muted-foreground xl:hidden">{t("details.lastUsedAtLabel")}:</span>{token_3.lastUsedAt ? formatDate(token_3.lastUsedAt, locale) : t("notUsed")}</span><Button type="button" variant="ghost" size="icon-sm" aria-label={`${t(isExpanded ? "details.collapse" : "details.expand")} ${token_3.name}`} aria-expanded={isExpanded} aria-controls={`personal-access-token-details-${token_3.id}`} onClick={() => setExpandedId(isExpanded ? null : token_3.id)}><IconChevronRight aria-hidden="true" className={isExpanded ? "rotate-90" : ""} /></Button></div>{isExpanded && <div id={`personal-access-token-details-${token_3.id}`} className="grid gap-4 border-t bg-muted/20 px-4 py-4 sm:grid-cols-2 xl:grid-cols-4"><TokenDetail label={t("details.ownerLabel")}>{t("owner")}</TokenDetail><TokenDetail label={t("details.issuedByLabel")}>{token_3.issuedBy}</TokenDetail><TokenDetail label={t("details.createdAtLabel")}>{formatDate(token_3.createdAt, locale)}</TokenDetail><TokenDetail label={t("details.usageCountLabel")}>{token_3.usageCount}</TokenDetail><div className="flex justify-end sm:col-span-2 xl:col-span-4"><DropdownMenu><DropdownMenuTrigger asChild={true}><Button type="button" variant="outline" size="sm" aria-label={`${t("actions.open")} ${token_3.name}`}><IconDotsVertical aria-hidden="true" />{t("actions.open")}</Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem onSelect={() => setPendingAction({
                    token: token_3,
                    action: "rename"
                  })}><IconPencil aria-hidden="true" />{t("actions.rename")}</DropdownMenuItem>{token_3.status !== "revoked" && <DropdownMenuItem onSelect={() => setPendingAction({
                    token: token_3,
                    action: "revoke"
                  })}><IconBan aria-hidden="true" />{t("actions.revoke")}</DropdownMenuItem>}<DropdownMenuItem variant="destructive" onSelect={() => setPendingAction({
                    token: token_3,
                    action: "delete"
                  })}><IconTrash aria-hidden="true" />{t("actions.delete")}</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div></div>}</article>;
      })}</div>;
    $[69] = expandedId;
    $[70] = locale;
    $[71] = t;
    $[72] = visibleTokens;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  const t22 = pendingAction?.token ?? null;
  const t23 = pendingAction?.action ?? null;
  let t24;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setPendingAction(null);
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t) {
    t25 = () => setStatusMessage(t("alreadyRevoked"));
    $[75] = t;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== isPending || $[78] !== onDelete || $[79] !== onRename || $[80] !== onRevoke || $[81] !== t22 || $[82] !== t23 || $[83] !== t25) {
    t26 = <PersonalAccessTokenActionDialogs token={t22} action={t23} isPending={isPending} onClose={t24} onRename={onRename} onRevoke={onRevoke} onAlreadyRevoked={t25} onDelete={onDelete} />;
    $[77] = isPending;
    $[78] = onDelete;
    $[79] = onRename;
    $[80] = onRevoke;
    $[81] = t22;
    $[82] = t23;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== statusAnnouncement || $[86] !== t20 || $[87] !== t21 || $[88] !== t26) {
    t27 = <div className="space-y-4">{statusAnnouncement}{t20}{t21}{t26}</div>;
    $[85] = statusAnnouncement;
    $[86] = t20;
    $[87] = t21;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  return t27;
};
const TokenDetail = t0 => {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <p className="text-sm break-words">{children}</p>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0 space-y-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(token) {
  return !token.deletedAt;
}
function _temp2(token_2) {
  return token_2.deletedAt;
}